'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import { 
  Upload, Check, ChevronRight, ChevronLeft, 
  Play, Pause, Trash2, ShieldCheck, FileText, Music2, Lock,
  AlertCircle, Shield, Bot, Info, CheckCircle2, ToggleLeft, ToggleRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

const STEPS = [
  { id: 1, title: 'Release', desc: 'Metadata & Cover Art' },
  { id: 2, title: 'Tracks', desc: 'Audio & Song Details' },
  { id: 3, title: 'Stores', desc: 'DSPs & Content ID' },
  { id: 4, title: 'Rights & AI', desc: 'Declaration & Policies' },
  { id: 5, title: 'Review', desc: 'Submit for QC' },
];

const GENRES = [
  'Devotional / Spiritual', 'Bollywood / Film', 'Indian Classical (Hindustani / Carnatic)', 
  'Indian Folk / Traditional', 'Desi Hip-Hop / Rap', 'Indian Pop (Indipop)', 'Sufi / Qawwali', 
  'Ghazal', 'Regional Folk', 'Pop', 'Hip-Hop / Rap', 'R&B / Soul', 'Electronic / Dance / EDM', 
  'Rock', 'Metal', 'Jazz', 'Acoustic / Singer-Songwriter', 'Ambient / Meditation', 'Lo-Fi / Chillhop',
  'Classical (Western)', 'Latin / Reggaeton', 'World / Global Music', 'Instrumental / Soundtrack'
];

const SUBGENRES: Record<string, string[]> = {
  'Devotional / Spiritual': ['Bhajan', 'Aarti', 'Mantra', 'Stotram / Stuti', 'Kirtan / Dhun', 'Chalisa', 'Shloka', 'Devotional Pop'],
  'Bollywood / Film': ['Romantic Melody', 'Dance Track', 'Sad / Emotional', 'Item Song', 'Patriotic', 'Retro Bollywood'],
  'Indian Classical (Hindustani / Carnatic)': ['Hindustani Vocal', 'Carnatic Vocal', 'Khayal', 'Dhrupad', 'Instrumental Solo'],
  'Indian Folk / Traditional': ['Marathi (Lavani / Powada / Gondhal)', 'Punjabi Folk / Bhangra', 'Rajasthani Folk', 'Garba / Dandiya', 'Bengali Baul'],
  'Desi Hip-Hop / Rap': ['Gully Rap', 'Trap Hip-Hop', 'Drill', 'Regional Street Rap', 'Old School Boom Bap'],
  'Indian Pop (Indipop)': ['Indie Pop Ballad', 'Dance Pop', 'Electro Indipop', 'Acoustic Pop', 'Lo-Fi Pop'],
  'Sufi / Qawwali': ['Traditional Qawwali', 'Sufi Rock', 'Sufi Pop', 'Kafi'],
  'Pop': ['Contemporary Pop', 'Dance Pop', 'Synth-Pop', 'Electropop'],
  'Hip-Hop / Rap': ['Trap', 'Boom Bap', 'Drill', 'Conscious Rap'],
  'Electronic / Dance / EDM': ['House', 'Techno', 'Trance / Psytrance', 'Future Bass', 'Dubstep'],
  'Rock': ['Classic Rock', 'Alternative Rock', 'Hard Rock', 'Indie Rock'],
  'Lo-Fi / Chillhop': ['Lo-Fi Beats', 'Chillhop', 'Jazzhop', 'Synthwave']
};

const LANGUAGES = [
  'Marathi', 'Hindi', 'English', 'Sanskrit', 'Punjabi', 'Bhojpuri', 
  'Bengali', 'Gujarati', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Urdu', 'Instrumental'
];

const AI_TOOLS = [
  'Suno AI', 'Udio', 'Soundraw', 'AIVA', 'Boomy',
  'Mubert', 'Loudly', 'Beatoven.ai', 'Other AI Tool'
];

const DSP_STORES = [
  { id: 'spotify', name: 'Spotify', bg: '#1DB954' },
  { id: 'apple', name: 'Apple Music', bg: '#FC3C44' },
  { id: 'youtube', name: 'YouTube Music', bg: '#FF0000' },
  { id: 'instagram', name: 'Instagram & Facebook', bg: '#E1306C' },
  { id: 'amazon', name: 'Amazon Music', bg: '#00A8E1' },
  { id: 'tiktok', name: 'TikTok / ByteDance', bg: '#000000' },
  { id: 'jiosaavn', name: 'JioSaavn', bg: '#2BC5B4' },
  { id: 'wynk', name: 'Wynk Music', bg: '#ED1C24' },
  { id: 'deezer', name: 'Deezer', bg: '#FEAA2D' },
  { id: 'pandora', name: 'Pandora', bg: '#005483' },
  { id: 'beatport', name: 'Beatport', bg: '#01FF95' },
  { id: 'audiomack', name: 'Audiomack', bg: '#FFA200' },
  { id: 'soundcloud', name: 'SoundCloud', bg: '#FF5500' },
  { id: 'iheart', name: 'iHeartRadio', bg: '#C61F26' },
  { id: 'anghami', name: 'Anghami', bg: '#D10056' },
  { id: 'boomplay', name: 'Boomplay', bg: '#00ADEF' },
  { id: 'tencent', name: 'Tencent Music', bg: '#31C27C' },
  { id: 'netease', name: 'NetEase Cloud', bg: '#C20C0C' }
];

export default function CreateReleasePage() {
  const router = useRouter();
  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userPlan, setUserPlan] = useState('free');

  // Step 1: Release Metadata
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [genre, setGenre] = useState('');
  const [subgenre, setSubgenre] = useState('');
  const [primaryArtist, setPrimaryArtist] = useState('');
  const [physicalReleaseDate, setPhysicalReleaseDate] = useState('');
  const [storeReleaseDate, setStoreReleaseDate] = useState('');
  const [cpLine, setCpLine] = useState('');
  const [productionYear, setProductionYear] = useState('');
  const [coverArtworkUrl, setCoverArtworkUrl] = useState('');
  const artworkFileRef = useRef<HTMLInputElement>(null);

  // Step 2: Tracks Form Data
  const [trackTitle, setTrackTitle] = useState('');
  const [writers, setWriters] = useState('');
  const [composers, setComposers] = useState('');
  const [musicDirectors, setMusicDirectors] = useState('');
  const [producers, setProducers] = useState('');
  const [language, setLanguage] = useState('');
  const [isrc, setIsrc] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [lyrics, setLyrics] = useState('');
  const [showLyricsModal, setShowLyricsModal] = useState(false);
  
  // Real Audio Player State
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [audioObjectUrl, setAudioObjectUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);

  const [isExplicit, setIsExplicit] = useState<boolean>(false);
  const [isInstrumental, setIsInstrumental] = useState<boolean>(false);

  // Step 3: Stores & Content ID
  const [selectedStores, setSelectedStores] = useState<string[]>(DSP_STORES.map((s) => s.id));
  const [youtubeContentId, setYoutubeContentId] = useState<boolean>(true);
  const [metaContentId, setMetaContentId] = useState<boolean>(true);

  // Step 4: Rights & AI Declaration
  const [isAiGenerated, setIsAiGenerated] = useState<'no' | 'yes' | ''>('');
  const [aiTool, setAiTool] = useState('');
  const [aiCreationDate, setAiCreationDate] = useState('');
  const [aiCommercialRights, setAiCommercialRights] = useState(false);
  const [aiDocFile, setAiDocFile] = useState<File | null>(null);
  const [aiAdditionalInfo, setAiAdditionalInfo] = useState('');
  const [confirmRights, setConfirmRights] = useState(false);
  const [confirmPolicies, setConfirmPolicies] = useState(false);
  const aiDocRef = useRef<HTMLInputElement>(null);

  const loadUserData = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        router.push('/sign-up-login-screen');
        return;
      }

      const { data: profile } = await supabase.from('user_profiles').select('*').eq('id', user.id).maybeSingle();
      const { data: sub } = await supabase.from('subscriptions').select('plan').eq('user_id', user.id).maybeSingle();

      const plan = sub?.plan || profile?.plan || 'free';
      setUserPlan(plan);

      const artistName = profile?.artist_name || profile?.full_name || 'Santosh Katkade';
      setPrimaryArtist(artistName);
      setCpLine(artistName);
      setProductionYear(new Date().getFullYear().toString());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [supabase, router]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const isLabelPlan = userPlan === 'label_producer' || userPlan === 'LABEL';

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error('Invalid format! Only JPG or PNG files are permitted.');
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.src = objectUrl;

    img.onload = () => {
      if (img.width !== 3000 || img.height !== 3000) {
        toast.error(`Image size mismatch! Size is ${img.width}x${img.height} px. Only exactly 3000 x 3000 px is accepted.`);
        setCoverArtworkUrl('');
        if (artworkFileRef.current) artworkFileRef.current.value = '';
      } else {
        setCoverArtworkUrl(objectUrl);
        toast.success('Cover artwork verified (3000 x 3000 px) and attached!');
      }
    };
  };

  const handleAudioUpload = (file: File) => {
    setAudioFile(file);
    const url = URL.createObjectURL(file);
    setAudioObjectUrl(url);
    setIsPlaying(false);
    toast.success(`Audio uploaded successfully: ${file.name}`);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => setIsPlaying(false));
    }
  };

  const toggleStore = (id: string) => {
    if (selectedStores.includes(id)) {
      setSelectedStores(selectedStores.filter((s) => s !== id));
    } else {
      setSelectedStores([...selectedStores, id]);
    }
  };

  const selectAllStores = () => setSelectedStores(DSP_STORES.map((s) => s.id));
  const deselectAllStores = () => setSelectedStores([]);

  const handleFinalSubmit = async () => {
    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const generatedUpc = `890${Math.floor(100000000 + Math.random() * 900000000)}`;
      const generatedIsrc = isrc.trim() || `IN-TNX-26-${Math.floor(10000 + Math.random() * 90000)}`;

      const payload = {
        user_id: user.id,
        title: title.trim(),
        version: subtitle.trim() || null,
        release_type: 'single',
        primary_artist: primaryArtist,
        artist: primaryArtist,
        label: 'Tubenexa Distribution',
        primary_genre: genre,
        subgenre: subgenre || null,
        language: language,
        release_date: storeReleaseDate,
        original_release_date: physicalReleaseDate || storeReleaseDate,
        c_year: productionYear,
        c_line: cpLine,
        p_year: productionYear,
        p_line: cpLine,
        producer: producers || primaryArtist,
        composer: composers || primaryArtist,
        lyricist: writers || primaryArtist,
        isrc: generatedIsrc,
        upc: generatedUpc,
        youtube_url: youtubeUrl || null,
        lyrics: isInstrumental ? null : (lyrics || null),
        is_instrumental: isInstrumental,
        is_explicit: isExplicit,
        youtube_content_id: youtubeContentId,
        meta_content_id: metaContentId,
        is_ai: isAiGenerated === 'yes',
        ai_tool: isAiGenerated === 'yes' ? aiTool : null,
        ai_creation_date: isAiGenerated === 'yes' ? aiCreationDate : null,
        ai_commercial_rights: isAiGenerated === 'yes' ? aiCommercialRights : false,
        ai_additional_info: isAiGenerated === 'yes' ? aiAdditionalInfo : null,
        stores_count: selectedStores.length,
        status: 'QC_PENDING',
        qc_score: 100,
        artwork_url: coverArtworkUrl || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=3000',
        created_at: new Date().toISOString(),
      };

      const { error } = await supabase.from('releases').insert([payload]);
      if (error) throw error;

      toast.success(`Release "${title}" submitted for TubeNexa Quality Control!`);
      router.push('/releases');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to submit release');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="py-24 text-center text-xs text-zinc-500">Loading release workspace...</div>
      </AppLayout>
    );
  }

  const inputClass = 'w-full px-4 py-2.5 rounded-xl bg-[#161a2b] border border-zinc-800 text-xs text-white placeholder-zinc-600 outline-none focus:border-purple-500 transition';

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-6 pb-20 font-sans">
        
        {/* Step Indicator Header */}
        <div className="p-4 rounded-2xl bg-[#111422] border border-zinc-800 shadow-xl flex items-center justify-between overflow-x-auto">
          {STEPS.map((s, idx) => {
            const isCurrent = currentStep === s.id;
            const isCompleted = currentStep > s.id;
            return (
              <React.Fragment key={s.id}>
                <div 
                  onClick={() => s.id <= currentStep && setCurrentStep(s.id)}
                  className={`flex items-center gap-3 cursor-pointer select-none px-4 py-2 rounded-xl transition ${
                    isCurrent ? 'bg-purple-950/40 border border-purple-800/60' : ''
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${
                    isCurrent ? 'bg-purple-600 text-white ring-4 ring-purple-500/20' : isCompleted ? 'bg-emerald-500 text-white' : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    {isCompleted ? <Check size={14} /> : s.id}
                  </div>
                  <div>
                    <div className={`text-xs font-bold ${isCurrent ? 'text-white' : isCompleted ? 'text-emerald-400' : 'text-zinc-400'}`}>
                      {s.title}
                    </div>
                    <div className="text-[11px] text-zinc-500">{s.desc}</div>
                  </div>
                </div>
                {idx < STEPS.length - 1 && <ChevronRight size={16} className="text-zinc-700 mx-2 flex-shrink-0" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* ─── STEP 1: RELEASE METADATA & COVER ART ─── */}
        {currentStep === 1 && (
          <div className="p-8 rounded-3xl bg-[#111422] border border-zinc-800 shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
              <div>
                <h2 className="text-base font-bold text-white">Release Metadata</h2>
                <p className="text-xs text-zinc-400">Enter title, genre classification, copyright publishing, and cover artwork.</p>
              </div>
              <button
                onClick={() => {
                  if (!title.trim()) { toast.error('Release title is compulsory (*)'); return; }
                  if (!genre) { toast.error('Genre & Subgenre is compulsory (*)'); return; }
                  if (!storeReleaseDate) { toast.error('Store Release Date is compulsory (*)'); return; }
                  if (!productionYear) { toast.error('Production Year is compulsory (*)'); return; }
                  if (!coverArtworkUrl) { toast.error('Cover Art (3000 x 3000 px) is compulsory (*)'); return; }
                  setCurrentStep(2);
                }}
                className="px-6 py-2.5 rounded-xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-600/30 transition flex items-center gap-2"
              >
                Next <ChevronRight size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">
                      Title <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={title}
                      onChange={(e) => { setTitle(e.target.value); setTrackTitle(e.target.value); }}
                      placeholder="e.g. Swami Samarth Tarak Mantra"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">Subtitle (Optional)</label>
                    <input
                      type="text"
                      value={subtitle}
                      onChange={(e) => setSubtitle(e.target.value)}
                      placeholder="Please provide a subtitle"
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-xs font-bold text-zinc-300">
                        Primary Artist <span className="text-rose-500">*</span>
                      </label>
                      {!isLabelPlan && (
                        <span className="text-2xs text-amber-400 flex items-center gap-1 font-semibold">
                          <Lock size={10} /> Locked to Account
                        </span>
                      )}
                    </div>
                    <input
                      type="text"
                      disabled={!isLabelPlan}
                      value={primaryArtist}
                      onChange={(e) => setPrimaryArtist(e.target.value)}
                      className={`${inputClass} ${!isLabelPlan ? 'bg-[#161a2b]/50 text-zinc-400 cursor-not-allowed' : ''}`}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">
                      Genre & Subgenre <span className="text-rose-500">*</span>
                    </label>
                    <select
                      value={genre}
                      onChange={(e) => { setGenre(e.target.value); setSubgenre(''); }}
                      className={`${inputClass} cursor-pointer`}
                    >
                      <option value="">-- Please select genre & subgenre --</option>
                      {GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>

                  {genre && SUBGENRES[genre] && (
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold text-zinc-300 block mb-1.5">Specific Subgenre</label>
                      <select
                        value={subgenre}
                        onChange={(e) => setSubgenre(e.target.value)}
                        className={`${inputClass} cursor-pointer`}
                      >
                        <option value="">-- Select specific subgenre --</option>
                        {SUBGENRES[genre].map((sg) => <option key={sg} value={sg}>{sg}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">Physical / Original Release Date</label>
                    <input
                      type="date"
                      value={physicalReleaseDate}
                      onChange={(e) => setPhysicalReleaseDate(e.target.value)}
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">
                      Store Release Date <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={storeReleaseDate}
                      onChange={(e) => setStoreReleaseDate(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">©&℗ Line</label>
                    <input
                      type="text"
                      value={cpLine}
                      onChange={(e) => setCpLine(e.target.value)}
                      placeholder="e.g. Rekha Katkade"
                      className={inputClass}
                    />
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">
                      Production Year <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={productionYear}
                      onChange={(e) => setProductionYear(e.target.value)}
                      placeholder="2026"
                      className={inputClass}
                    />
                  </div>
                </div>
              </div>

              {/* Cover Art Upload */}
              <div className="space-y-3">
                <label className="text-xs font-bold text-zinc-300 block mb-1.5">
                  Cover Art <span className="text-rose-500">*</span>
                </label>
                <div
                  onClick={() => artworkFileRef.current?.click()}
                  className="aspect-square rounded-2xl border-2 border-dashed border-zinc-800 hover:border-purple-500 bg-[#161a2b]/60 cursor-pointer flex flex-col items-center justify-center p-6 text-center transition relative overflow-hidden"
                >
                  <input
                    ref={artworkFileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  {coverArtworkUrl ? (
                    <img src={coverArtworkUrl} alt="Cover Preview" className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <div className="space-y-2">
                      <div className="w-12 h-12 rounded-2xl bg-purple-600/10 text-purple-400 flex items-center justify-center mx-auto">
                        <Upload size={24} />
                      </div>
                      <div className="text-xs font-bold text-white">Click or drag file to this area to upload</div>
                      <div className="text-2xs text-zinc-500">Only square 3000 x 3000 px JPG/PNG allowed.</div>
                    </div>
                  )}
                </div>

                <div className="p-3.5 rounded-xl bg-zinc-900/90 border border-zinc-800/80 space-y-1.5 text-2xs">
                  <div className="font-bold text-zinc-300 flex items-center gap-1.5">
                    <Info size={13} className="text-purple-400" /> Cover Art Guidelines
                  </div>
                  <ul className="text-zinc-400 space-y-1 list-disc pl-4 leading-relaxed">
                    <li>Strict resolution: <strong>3000 x 3000 px</strong> (Square 1:1)</li>
                    <li>Format: High resolution <strong>JPG or PNG</strong> (RGB mode)</li>
                    <li>No social media handles, phone numbers, prices, or store logos</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 2: TRACKS INFO & REAL AUDIO PLAYER ─── */}
        {currentStep === 2 && (
          <div className="p-8 rounded-3xl bg-[#111422] border border-zinc-800 shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-zinc-800 pb-4">
              <div>
                <h2 className="text-base font-bold text-white">Tracks Information</h2>
                <p className="text-xs text-zinc-400">Upload master audio file and enter track contributors.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-5 py-2.5 rounded-xl font-bold text-xs bg-zinc-900 text-zinc-300 hover:text-white"
                >
                  Back
                </button>
                <button
                  onClick={() => {
                    if (!audioFile) { toast.error('Audio file upload is compulsory (*)'); return; }
                    if (!trackTitle.trim()) { toast.error('Track title is compulsory (*)'); return; }
                    if (!language) { toast.error('Track Language is compulsory (*)'); return; }
                    setCurrentStep(3);
                  }}
                  className="px-6 py-2.5 rounded-xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-600/30 transition flex items-center gap-2"
                >
                  Save & Next <ChevronRight size={16} />
                </button>
              </div>
            </div>

            {/* Audio Upload Drop Area */}
            <div
              onClick={() => audioInputRef.current?.click()}
              className="p-8 rounded-2xl border-2 border-dashed border-zinc-800 hover:border-purple-500 bg-[#161a2b]/50 cursor-pointer flex flex-col items-center justify-center text-center gap-2"
            >
              <input
                ref={audioInputRef}
                type="file"
                accept=".wav,.flac,.mp3"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleAudioUpload(f);
                }}
              />
              <div className="w-12 h-12 rounded-2xl bg-purple-600/10 text-purple-400 flex items-center justify-center">
                <Music2 size={24} />
              </div>
              <div className="text-xs font-bold text-white">
                {audioFile ? `Selected: ${audioFile.name}` : 'Click or drag audio file to upload'}
              </div>
              <div className="text-2xs text-zinc-500">WAV or FLAC lossless audio is strictly recommended.</div>
            </div>

            {/* REAL AUDIO PLAYER CONTAINER (SHOWS ONLY AFTER UPLOAD) */}
            {audioFile && audioObjectUrl && (
              <div className="p-6 rounded-2xl bg-[#161a2b] border border-zinc-800 space-y-5 animate-in fade-in">
                <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
                  <div className="text-xs font-bold text-emerald-400 flex items-center gap-1.5">
                    <CheckCircle2 size={14} /> {audioFile.name} ({(audioFile.size / (1024 * 1024)).toFixed(2)} MB)
                  </div>
                  <button 
                    onClick={() => {
                      setAudioFile(null);
                      setAudioObjectUrl(null);
                      setIsPlaying(false);
                      if (audioRef.current) audioRef.current.pause();
                      if (audioInputRef.current) audioInputRef.current.value = '';
                      toast.success('Audio removed successfully');
                    }} 
                    className="text-rose-400 hover:text-rose-300 px-2.5 py-1 rounded-lg bg-rose-950/40 border border-rose-800/60 flex items-center gap-1 text-2xs cursor-pointer font-bold"
                    title="Delete and Change Audio"
                  >
                    <Trash2 size={13} /> Change / Remove Audio
                  </button>
                </div>

                {/* HTML5 Audio Element */}
                <audio 
                  ref={audioRef} 
                  src={audioObjectUrl} 
                  onEnded={() => setIsPlaying(false)} 
                  onPause={() => setIsPlaying(false)}
                  onPlay={() => setIsPlaying(true)}
                />

                <div className="flex items-center gap-4 bg-[#111422] p-3 rounded-xl border border-zinc-800">
                  <button
                    type="button"
                    onClick={togglePlay}
                    className="w-9 h-9 rounded-full bg-purple-600 text-white flex items-center justify-center shadow-md cursor-pointer"
                  >
                    {isPlaying ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
                  </button>
                  <span className="text-2xs font-mono text-zinc-400">Audio Preview</span>
                  <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                    <div className="h-full bg-purple-600 w-full animate-pulse" />
                  </div>
                  <span className="text-2xs font-mono text-emerald-400 font-bold">Ready</span>
                </div>
              </div>
            )}

            {/* Blank Track Metadata Inputs */}
            <div className="p-6 rounded-2xl bg-[#161a2b] border border-zinc-800 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">
                    Track Title <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={trackTitle}
                    onChange={(e) => setTrackTitle(e.target.value)}
                    placeholder="Enter track title"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">
                    Primary Artist <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    disabled={!isLabelPlan}
                    value={primaryArtist}
                    onChange={(e) => setPrimaryArtist(e.target.value)}
                    className={`${inputClass} ${!isLabelPlan ? 'bg-[#111422]/50 text-zinc-400 cursor-not-allowed' : ''}`}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">Writers</label>
                  <input
                    type="text"
                    value={writers}
                    onChange={(e) => setWriters(e.target.value)}
                    placeholder="Writers legal names"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">Composers</label>
                  <input
                    type="text"
                    value={composers}
                    onChange={(e) => setComposers(e.target.value)}
                    placeholder="Composers legal names"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">Music Directors</label>
                  <input
                    type="text"
                    value={musicDirectors}
                    onChange={(e) => setMusicDirectors(e.target.value)}
                    placeholder="Music Directors"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">Producers</label>
                  <input
                    type="text"
                    value={producers}
                    onChange={(e) => setProducers(e.target.value)}
                    placeholder="Producer names"
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">
                    Language <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className={`${inputClass} cursor-pointer`}
                  >
                    <option value="">-- Select Language --</option>
                    {LANGUAGES.map((l) => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">Lyrics</label>
                  <button
                    type="button"
                    onClick={() => setShowLyricsModal(true)}
                    className="w-full px-4 py-2.5 rounded-xl bg-[#111422] border border-zinc-800 text-xs text-zinc-300 hover:text-white flex items-center justify-between cursor-pointer"
                  >
                    <span>{lyrics ? 'Lyrics Added ✓' : 'Add Lyrics'}</span>
                    <FileText size={14} className="text-purple-400" />
                  </button>
                </div>
              </div>

              {/* Explicit & Instrumental Toggles */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-zinc-800">
                <div className="p-3.5 rounded-xl bg-[#111422] border border-zinc-800 flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold text-white">Explicit Lyrics / Content</div>
                    <div className="text-2xs text-zinc-500">Track contains adult/explicit language.</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setIsExplicit(false)}
                      className={`px-3 py-1 rounded-lg text-2xs font-bold cursor-pointer ${!isExplicit ? 'bg-emerald-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsExplicit(true)}
                      className={`px-3 py-1 rounded-lg text-2xs font-bold cursor-pointer ${isExplicit ? 'bg-rose-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}
                    >
                      Yes
                    </button>
                  </div>
                </div>

                <div className="p-3.5 rounded-xl bg-[#111422] border border-zinc-800 flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold text-white">Instrumental Track</div>
                    <div className="text-2xs text-zinc-500">Recording contains no vocal lyrics.</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setIsInstrumental(false)}
                      className={`px-3 py-1 rounded-lg text-2xs font-bold cursor-pointer ${!isInstrumental ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}
                    >
                      No
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsInstrumental(true)}
                      className={`px-3 py-1 rounded-lg text-2xs font-bold cursor-pointer ${isInstrumental ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400'}`}
                    >
                      Yes
                    </button>
                  </div>
                </div>
              </div>

              {/* Separated ISRC and YouTube URL */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-zinc-800">
                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">ISRC Code</label>
                  <input
                    type="text"
                    value={isrc}
                    onChange={(e) => setIsrc(e.target.value)}
                    placeholder="Leave blank to auto-generate"
                    className={`${inputClass} font-mono`}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">YouTube URL (Optional Reference)</label>
                  <input
                    type="text"
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className={inputClass}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── STEP 3: STORES & CONTENT ID ─── */}
        {currentStep === 3 && (
          <div className="p-8 rounded-3xl bg-[#111422] border border-zinc-800 shadow-2xl space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-zinc-800 pb-4">
              <div>
                <h2 className="text-base font-bold text-white">Select Distribution Stores</h2>
                <p className="text-xs text-zinc-400">Choose DSP platforms and configure Content ID monetization.</p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={selectAllStores}
                  className="px-3.5 py-1.5 rounded-lg text-2xs font-bold bg-[#161a2b] border border-zinc-800 text-emerald-400 hover:bg-zinc-800 cursor-pointer"
                >
                  ✓ Select All
                </button>
                <button
                  onClick={deselectAllStores}
                  className="px-3.5 py-1.5 rounded-lg text-2xs font-bold bg-[#161a2b] border border-zinc-800 text-rose-400 hover:bg-zinc-800 cursor-pointer"
                >
                  ✕ Deselect All
                </button>
              </div>
            </div>

            {/* DSP Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-4">
              {DSP_STORES.map((st) => {
                const isSelected = selectedStores.includes(st.id);
                return (
                  <div
                    key={st.id}
                    onClick={() => toggleStore(st.id)}
                    className={`p-4 rounded-2xl border cursor-pointer flex flex-col items-center justify-between gap-3 text-center transition duration-200 select-none ${
                      isSelected
                        ? 'bg-purple-950/20 border-purple-500 shadow-lg shadow-purple-500/10'
                        : 'bg-[#161a2b]/60 border-zinc-800/80 opacity-50 hover:opacity-100'
                    }`}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white text-xs shadow-md" style={{ backgroundColor: st.bg }}>
                      {st.name.slice(0, 2)}
                    </div>
                    <span className="text-xs font-semibold text-white truncate max-w-full">{st.name}</span>
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${isSelected ? 'bg-emerald-500 text-white' : 'bg-zinc-800 text-zinc-500'}`}>
                      {isSelected ? '✓' : ''}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Content ID Toggles Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-zinc-800">
              <div className="p-4 rounded-2xl bg-[#161a2b] border border-zinc-800 flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-white flex items-center gap-1.5">
                    <ShieldCheck size={14} className="text-purple-400" /> YouTube Content ID
                  </div>
                  <p className="text-2xs text-zinc-400 mt-0.5">Monetize re-uploads across YouTube fingerprinting.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setYoutubeContentId(!youtubeContentId)}
                  className="transition cursor-pointer"
                >
                  {youtubeContentId ? <ToggleRight size={32} className="text-purple-500" /> : <ToggleLeft size={32} className="text-zinc-600" />}
                </button>
              </div>

              <div className="p-4 rounded-2xl bg-[#161a2b] border border-zinc-800 flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-white flex items-center gap-1.5">
                    <ShieldCheck size={14} className="text-blue-400" /> Meta (IG / FB) Content ID
                  </div>
                  <p className="text-2xs text-zinc-400 mt-0.5">Claim monetization across Instagram Reels & FB.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setMetaContentId(!metaContentId)}
                  className="transition cursor-pointer"
                >
                  {metaContentId ? <ToggleRight size={32} className="text-blue-500" /> : <ToggleLeft size={32} className="text-zinc-600" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-5 py-2.5 rounded-xl font-bold text-xs bg-zinc-900 text-zinc-300 hover:text-white cursor-pointer"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (selectedStores.length === 0) { toast.error('Please select at least 1 store'); return; }
                  setCurrentStep(4);
                }}
                className="px-6 py-2.5 rounded-xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-600/30 transition flex items-center gap-2 cursor-pointer"
              >
                Continue to Rights & AI <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 4: RIGHTS & AI DECLARATION ─── */}
        {currentStep === 4 && (
          <div className="p-8 rounded-3xl bg-[#111422] border border-zinc-800 shadow-2xl space-y-6">
            <div>
              <h2 className="text-base font-bold text-white">Rights & AI Declaration</h2>
              <p className="text-xs text-zinc-400 mt-0.5">You must accurately declare the rights status of your release and disclose any use of AI music generation tools.</p>
            </div>

            <div className="p-4 rounded-2xl bg-purple-950/20 border border-purple-800/40 space-y-1.5 text-2xs">
              <div className="font-bold text-purple-300 flex items-center gap-1.5">
                <Bot size={15} /> TubeNexa AI Music Policy
              </div>
              <p className="text-zinc-300 leading-relaxed">
                TubeNexa accepts eligible AI-assisted and AI-generated music subject to rights verification, quality control, and downstream distributor and DSP requirements. AI music is reviewed before distribution. Acceptance is not guaranteed, and individual DSP and store policies may apply.
              </p>
            </div>

            <div className="space-y-3">
              <label className="text-xs font-bold text-zinc-300 block">
                Was this release created or assisted using generative AI tools? <span className="text-rose-500">*</span>
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setIsAiGenerated('no')}
                  className={`p-4 rounded-2xl border text-left space-y-1 transition cursor-pointer ${
                    isAiGenerated === 'no' 
                      ? 'bg-emerald-950/30 border-emerald-500 text-emerald-300' 
                      : 'bg-[#161a2b] border-zinc-800 text-zinc-400'
                  }`}
                >
                  <span className="text-xs font-bold block">🛡️ No — Human created</span>
                  <span className="text-2xs text-zinc-400 block">This release was created entirely by human artists without AI generation tools.</span>
                </button>

                <button
                  type="button"
                  onClick={() => setIsAiGenerated('yes')}
                  className={`p-4 rounded-2xl border text-left space-y-1 transition cursor-pointer ${
                    isAiGenerated === 'yes' 
                      ? 'bg-purple-950/30 border-purple-500 text-purple-300' 
                      : 'bg-[#161a2b] border-zinc-800 text-zinc-400'
                  }`}
                >
                  <span className="text-xs font-bold block">✨ Yes — AI assisted/generated</span>
                  <span className="text-2xs text-zinc-400 block">This release was created with or assisted by generative AI tools.</span>
                </button>
              </div>
            </div>

            {/* AI Fields */}
            {isAiGenerated === 'yes' && (
              <div className="p-6 rounded-2xl bg-[#161a2b] border border-zinc-800 space-y-4 animate-in fade-in">
                <div className="p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-2xs text-amber-300 flex items-start gap-2">
                  <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                  <span>AI-generated releases undergo additional review. You must confirm that you hold commercial distribution rights for all AI-generated content. Providing false information may result in permanent account suspension.</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">
                      AI Tool / Provider <span className="text-rose-500">*</span>
                    </label>
                    <select
                      value={aiTool}
                      onChange={(e) => setAiTool(e.target.value)}
                      className={`${inputClass} cursor-pointer`}
                    >
                      <option value="">Select AI tool</option>
                      {AI_TOOLS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-zinc-300 block mb-1.5">
                      AI Creation Date <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={aiCreationDate}
                      onChange={(e) => setAiCreationDate(e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl bg-[#111422] border border-zinc-800">
                  <input
                    type="checkbox"
                    checked={aiCommercialRights}
                    onChange={(e) => setAiCommercialRights(e.target.checked)}
                    className="w-4 h-4 accent-purple-600 rounded mt-0.5"
                  />
                  <span className="text-xs text-zinc-300 leading-relaxed">
                    I confirm that the AI tool used ({aiTool || 'selected tool'}) grants commercial distribution rights for AI-generated content, and I hold those rights for this release.
                  </span>
                </label>

                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">Rights Documentation (Optional)</label>
                  <div
                    onClick={() => aiDocRef.current?.click()}
                    className="p-3.5 rounded-xl border border-dashed border-zinc-800 bg-[#111422] hover:border-purple-500 cursor-pointer flex items-center justify-between"
                  >
                    <input
                      ref={aiDocRef}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      className="hidden"
                      onChange={(e) => setAiDocFile(e.target.files?.[0] || null)}
                    />
                    <span className="text-xs text-zinc-400">{aiDocFile ? aiDocFile.name : 'Upload PDF, image, or receipt document'}</span>
                    <Upload size={14} className="text-purple-400" />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-zinc-300 block mb-1.5">Additional Information</label>
                  <textarea
                    rows={3}
                    value={aiAdditionalInfo}
                    onChange={(e) => setAiAdditionalInfo(e.target.value)}
                    placeholder="Describe how AI was used in creating this release..."
                    className={`${inputClass} resize-none`}
                  />
                </div>
              </div>
            )}

            {/* Rights Confirmations */}
            <div className="space-y-3 pt-3 border-t border-zinc-800">
              <div className="text-2xs font-bold uppercase tracking-wider text-purple-400">Rights Confirmations</div>

              <label className="flex items-start gap-3 cursor-pointer p-3.5 rounded-xl bg-[#161a2b] border border-zinc-800">
                <input
                  type="checkbox"
                  checked={confirmRights}
                  onChange={(e) => setConfirmRights(e.target.checked)}
                  className="w-4 h-4 accent-purple-600 rounded mt-0.5"
                />
                <span className="text-xs text-zinc-300 leading-relaxed">
                  I confirm that I own or control all necessary rights and permissions to distribute this content, including composition rights, master recording rights, and any featured artist rights. I am not distributing content that belongs to another rights holder without authorization.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer p-3.5 rounded-xl bg-[#161a2b] border border-zinc-800">
                <input
                  type="checkbox"
                  checked={confirmPolicies}
                  onChange={(e) => setConfirmPolicies(e.target.checked)}
                  className="w-4 h-4 accent-purple-600 rounded mt-0.5"
                />
                <span className="text-xs text-zinc-300 leading-relaxed">
                  I understand that TubeNexa Distribution and downstream digital service providers may reject content that does not meet their policies, technical standards, or content guidelines. Rejection does not entitle me to a refund of any paid plan fees.
                </span>
              </label>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
              <button
                onClick={() => setCurrentStep(3)}
                className="px-5 py-2.5 rounded-xl font-bold text-xs bg-zinc-900 text-zinc-300 hover:text-white cursor-pointer"
              >
                Back
              </button>
              <button
                onClick={() => {
                  if (!isAiGenerated) { toast.error('Please declare AI tool usage (*)'); return; }
                  if (isAiGenerated === 'yes' && (!aiTool || !aiCreationDate || !aiCommercialRights)) {
                    toast.error('Please complete all compulsory AI declaration fields (*)');
                    return;
                  }
                  if (!confirmRights || !confirmPolicies) { toast.error('Please accept both Rights Confirmations (*)'); return; }
                  setCurrentStep(5);
                }}
                className="px-6 py-2.5 rounded-xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-600/30 transition flex items-center gap-2 cursor-pointer"
              >
                Continue to Review <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ─── STEP 5: REVIEW & FINAL QC SUBMISSION ─── */}
        {currentStep === 5 && (
          <div className="p-8 rounded-3xl bg-[#111422] border border-zinc-800 shadow-2xl space-y-6">
            <div>
              <h2 className="text-base font-bold text-white">Review & Submit for Quality Control</h2>
              <p className="text-xs text-zinc-400 mt-0.5">Please review your metadata before final submission to TubeNexa quality control.</p>
            </div>

            <div className="p-6 rounded-2xl bg-[#161a2b] border border-zinc-800 space-y-4">
              <div className="flex items-center gap-4">
                <img
                  src={coverArtworkUrl || 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=3000'}
                  alt="Cover"
                  className="w-16 h-16 rounded-xl object-cover border border-zinc-800 shadow-md"
                />
                <div>
                  <h3 className="text-base font-bold text-white">{title || 'Untitled Release'}</h3>
                  <p className="text-xs text-purple-400 font-semibold">{primaryArtist}</p>
                  <p className="text-2xs text-zinc-400 mt-0.5">Tubenexa Distribution • {genre} • {language}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-zinc-800 text-2xs">
                <div>
                  <span className="text-zinc-500 block">Release Date</span>
                  <span className="font-bold text-white">{storeReleaseDate}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">YouTube Content ID</span>
                  <span className="font-bold text-emerald-400">{youtubeContentId ? 'Enabled' : 'Disabled'}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Content Rating</span>
                  <span className="font-bold text-white">{isExplicit ? 'Explicit' : 'Clean'}</span>
                </div>
                <div>
                  <span className="text-zinc-500 block">Stores Selected</span>
                  <span className="font-bold text-purple-400">{selectedStores.length} Stores</span>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
              <button
                onClick={() => setCurrentStep(4)}
                className="px-5 py-2.5 rounded-xl font-bold text-xs bg-zinc-900 text-zinc-300 hover:text-white cursor-pointer"
              >
                Back
              </button>
              <button
                disabled={submitting}
                onClick={handleFinalSubmit}
                className="px-8 py-3.5 rounded-2xl font-bold text-xs text-white bg-purple-600 hover:bg-purple-700 shadow-xl shadow-purple-600/30 transition disabled:opacity-50 flex items-center gap-2 cursor-pointer"
              >
                {submitting ? 'Submitting to Quality Control...' : 'Submit for QC Review 🚀'}
              </button>
            </div>
          </div>
        )}

        {/* Modal for Lyrics */}
        {showLyricsModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#111422] border border-zinc-800 rounded-3xl p-6 max-w-lg w-full space-y-4 shadow-2xl">
              <h3 className="text-sm font-bold text-white">Add / Edit Lyrics</h3>
              <textarea
                rows={10}
                value={lyrics}
                onChange={(e) => setLyrics(e.target.value)}
                placeholder="[Verse 1]&#10;Enter track lyrics here..."
                className="w-full p-4 rounded-2xl bg-[#161a2b] border border-zinc-800 text-xs text-white outline-none focus:border-purple-500 font-mono resize-none"
              />
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowLyricsModal(false)}
                  className="px-5 py-2 rounded-xl text-xs font-bold bg-purple-600 text-white cursor-pointer"
                >
                  Save Lyrics
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </AppLayout>
  );
}